// The market calendar (marketCalendar.js on the server): U.S. economic reports and big-company
// earnings. A page of its own, a "Today's market events" card in Morning prep, and a heads-up in
// the Live session shortly before a report. Times show in the trader's own time, with New York
// time beside them when that's different.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { livePosts } from './blog/posts';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { ding } from './adminAlerts';
import { syncMarketAlerts } from './pushClient';
import { PushHint } from './PushHint';

const NY = 'America/New_York';
const IMPORTANT_KEY = 'mm-market-important';
const cache = new Map();

const nyDay = (date = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: NY, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
const addDays = (key, n) => { const d = new Date(`${key}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// Events between two New York dates, fetched once every 10 minutes per range.
export function useMarketCalendar(from, to) {
  const key = `${from}|${to}`;
  const [data, setData] = useState(() => cache.get(key)?.data || null);
  useEffect(() => {
    if (!from) { setData(null); return undefined; }
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < 600000) { setData(hit.data); return undefined; }
    let alive = true;
    api(`/market-calendar?from=${from}&to=${to}`)
      .then((d) => { cache.set(key, { at: Date.now(), data: d }); if (alive) setData(d); })
      .catch(() => { if (alive) setData((old) => old || { events: [], failed: true }); });
    return () => { alive = false; };
  }, [key, from, to]);
  return data;
}

function useTexts() {
  const { t } = useI18n();
  return {
    title: (e) => (e.kind === 'earnings' ? t('{company} earnings', { company: e.company })
      : e.type === 'closed' ? `${t('Stock market closed')} · ${t(e.holiday)}` : t(e.title)),
    about: (e) => (e.kind === 'earnings' ? t('{company} reports its quarterly results. One of the biggest companies in the indexes.', { company: e.company }) : t(e.about)),
    when: (e) => {
      if (e.kind === 'earnings') return t(e.when);
      if (e.allDay) return t('All day');
      const at = new Date(e.at);
      const local = at.toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
      const ny = at.toLocaleTimeString(currentLocale(), { timeZone: NY, hour: 'numeric', minute: '2-digit' });
      return local === ny ? local : <>{local}<small>{ny} {t('New York')}</small></>;
    },
  };
}

function readImportant() {
  try { return localStorage.getItem(IMPORTANT_KEY) !== 'all'; } catch { return true; }
}

// The kinds of market event: everything, economic data releases (government reports and the Fed),
// or company earnings. Market holidays and early closes show under every kind.
export const EVENT_KINDS = [['all', 'All events'], ['econ', 'Economic data'], ['earnings', 'Earnings']];
// The same, for the Dashboard calendar's dropdown, where there's no room for the note.
export const EVENT_KIND_OPTIONS = [['all', 'All market events'], ['econ', 'Economic data'], ['earnings', 'Earnings (25 biggest companies)']];
export const ofKind = (kind) => (e) => kind === 'all' || e.kind === 'market' || e.kind === kind;
const KIND_KEY = 'mm-market-kind';
const readKind = () => { try { const k = localStorage.getItem(KIND_KEY); return EVENT_KINDS.some(([id]) => id === k) ? k : 'all'; } catch { return 'all'; } };

const IMPACT_LABEL = { high: 'High impact', medium: 'Medium impact', low: 'Low impact' };

function EventRow({ e, compact = false }) {
  const { t, lang } = useI18n();
  const x = useTexts();
  const live = livePosts().some((p) => p.slug === e.post);
  const past = new Date(e.at) < new Date();
  return (
    <li className={`mkt-event impact-${e.impact}${past ? ' past' : ''}`}>
      <span className="mkt-time">{x.when(e)}</span>
      <span className="mkt-main">
        <span className="mkt-title">
          <span className={`mkt-dot impact-${e.impact}`} title={t(IMPACT_LABEL[e.impact])} aria-label={t(IMPACT_LABEL[e.impact])} />
          <strong>{x.title(e)}</strong>
          {e.kind === 'earnings' && <span className="mkt-symbol">{e.symbol}</span>}
          {e.confirmed === false && <span className="mkt-expected" title={t('The company hasn’t announced this date yet.')}>{t('Expected')}</span>}
        </span>
        {!compact && <span className="mkt-about">{x.about(e)}</span>}
        <span className="mkt-meta">
          <span>{t(e.markets)}</span>
          {live && <Link to={`${lang === 'es' ? '/es' : ''}/blog/${e.post}`} target="_blank" rel="noopener noreferrer">{t('What is this?')} &rarr;</Link>}
          <AlertBell e={e} />
        </span>
      </span>
    </li>
  );
}

const dayTitle = (key) => {
  const text = new Date(`${key}T12:00:00Z`).toLocaleDateString(currentLocale(), { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Earnings cover 25 big companies, not the whole market; said wherever earnings show.
export function EarningsNote({ companies, compact = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const n = companies ? companies.length : 25;
  return (
    <p className={`mkt-earnings-note${compact ? ' compact' : ''}`}>
      {t('Earnings shown for {n} of the biggest companies in the S&P 500 and Nasdaq-100, not every company.', { n })}
      {companies && !compact && (
        <>
          {' '}<button type="button" className="link-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? t('Hide the list') : t('See the {n} companies', { n })}</button>
          {open && <span className="mkt-company-list">{companies.map((c) => `${c.name} (${c.symbol})`).join(' · ')}</span>}
        </>
      )}
    </p>
  );
}

// The full page: this week and next, grouped by day.
export function MarketCalendarPage() {
  const { t } = useI18n();
  const today = nyDay();
  const [weekOffset, setWeekOffset] = useState(0);
  const [important, setImportant] = useState(readImportant);
  const [kind, setKind] = useState(readKind);
  const chooseKind = (k) => { setKind(k); try { localStorage.setItem(KIND_KEY, k); } catch { /* not remembered */ } };
  const start = addDays(today, -new Date(`${today}T12:00:00Z`).getUTCDay() + weekOffset * 7);
  const end = addDays(start, 13);
  const data = useMarketCalendar(start, end);
  const choose = (v) => { setImportant(v); try { localStorage.setItem(IMPORTANT_KEY, v ? 'important' : 'all'); } catch { /* not remembered */ } };
  const events = (data ? data.events : []).filter(ofKind(kind)).filter((e) => !important || e.impact !== 'low');
  const days = [...new Set(events.map((e) => e.date))];
  const guide = livePosts().some((p) => p.slug === 'economic-calendar-for-traders');
  return (
    <div className="market-page">
      <div className="page-title page-title-row">
        <div>
          <p className="eyebrow">{t('Your day')}</p>
          <h1>{t('Market events')}</h1>
          <p className="subtitle">{t('The U.S. reports and big-company earnings that move futures. Times are in your time zone.')}</p>
        </div>
      </div>
      <div className="mkt-tabs" role="tablist" aria-label={t('Market events')}>
        {EVENT_KINDS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={kind === id} className={`mkt-tab${kind === id ? ' active' : ''}`} onClick={() => chooseKind(id)}>
            {t(label)}
          </button>
        ))}
      </div>
      {kind !== 'econ' && <EarningsNote companies={data && data.companies} />}
      <div className="mkt-toolbar">
        <div className="inbox-filter" role="group" aria-label={t('Show')}>
          <button type="button" className={`secondary small${important ? ' active' : ''}`} onClick={() => choose(true)}>{t('Important')}</button>
          <button type="button" className={`secondary small${!important ? ' active' : ''}`} onClick={() => choose(false)}>{t('Everything')}</button>
        </div>
        <div className="mkt-nav">
          <button type="button" className="secondary small" onClick={() => setWeekOffset((w) => w - 1)} aria-label={t('Previous week')}>&larr;</button>
          <button type="button" className="secondary small" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>{t('This week')}</button>
          <button type="button" className="secondary small" onClick={() => setWeekOffset((w) => w + 1)} aria-label={t('Next week')}>&rarr;</button>
        </div>
        <ul className="mkt-legend" aria-label={t('Impact')}>
          <li><span className="mkt-dot impact-high" />{t('High impact')}</li>
          <li><span className="mkt-dot impact-medium" />{t('Medium')}</li>
          <li><span className="mkt-dot impact-low" />{t('Low')}</li>
        </ul>
      </div>
      <AutoAlerts />
      {!data ? <p className="empty">{t('Loading…')}</p> : data.failed ? <p className="error">{t('Market events couldn’t load. Try again in a minute.')}</p> : (
        <>
          {!days.length && <p className="empty">{kind === 'earnings' ? t('No big-company earnings in these two weeks.') : t('No major reports in these two weeks.')}</p>}
          {days.map((d) => (
            <section key={d} className={`mkt-day${d === today ? ' today' : ''}${d < today ? ' past' : ''}`}>
              <h2>{dayTitle(d)}{d === today && <span className="mkt-today">{t('Today')}</span>}</h2>
              <ul className="mkt-list">{events.filter((e) => e.date === d).map((e) => <EventRow key={e.id} e={e} />)}</ul>
            </section>
          ))}
          <p className="mkt-foot">
            {t('Dates come from the official U.S. government, Federal Reserve and NYSE calendars and can change. Earnings dates come from each company; “Expected” means the company hasn’t confirmed it yet.')}
            {guide && <> <Link to="/blog/economic-calendar-for-traders">{t('How to trade around these reports')} &rarr;</Link></>}
          </p>
        </>
      )}
    </div>
  );
}

// Morning prep: today's important events (and tonight's earnings), with a link to the full calendar.
export function TodayEvents() {
  const { t } = useI18n();
  const today = nyDay();
  const data = useMarketCalendar(addDays(today, -1), addDays(today, 14));
  if (!data || data.failed) return null;
  const todays = data.events.filter((e) => e.date === today && e.impact !== 'low');
  const next = data.events.find((e) => e.date > today && e.impact === 'high');
  return (
    <section className="routine-card mkt-today-card" aria-labelledby="mkt-today-title">
      <div className="mkt-card-head">
        <h2 id="mkt-today-title">{t("Today's market events")}</h2>
        <Link to="/app/market-calendar" className="home-link">{t('All market events')} &rarr;</Link>
      </div>
      {todays.length ? (
        <>
          <ul className="mkt-list compact">{todays.map((e) => <EventRow key={e.id} e={e} compact />)}</ul>
          {todays.some((e) => e.kind === 'earnings') && <EarningsNote compact />}
        </>
      ) : (
        <p className="mkt-none">{t('No major reports today.')}</p>
      )}
      {next && (
        <p className="mkt-next">
          {t('Next big one: {title}, {day}', {
            title: next.kind === 'earnings' ? t('{company} earnings', { company: next.company }) : t(next.title),
            day: new Date(`${next.date}T12:00:00Z`).toLocaleDateString(currentLocale(), { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' }),
          })}
        </p>
      )}
    </section>
  );
}

// Live session: a heads-up from 15 minutes before a medium or high impact report until 5 minutes after.
export function SessionEventAlert() {
  const { t } = useI18n();
  const today = nyDay();
  const data = useMarketCalendar(addDays(today, -1), addDays(today, 14));
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 20000); return () => clearInterval(id); }, []);
  if (!data || data.failed) return null;
  const soon = data.events.filter((e) => e.kind === 'econ' && e.impact !== 'low' && e.type !== 'fomcPress')
    .find((e) => { const d = new Date(e.at) - now; return d <= 15 * 60000 && d > -5 * 60000; });
  if (!soon) return null;
  const mins = Math.round((new Date(soon.at) - now) / 60000);
  return (
    <div className={`session-event-alert impact-${soon.impact}`} role="status">
      <strong>{mins > 0 ? t('{title} in {n} min', { title: t(soon.title), n: mins }) : t('{title} just came out', { title: t(soon.title) })}</strong>
      <span>{mins > 0 ? t('Expect fast moves and wider spreads. Check your stop, or stay flat until it passes.') : t('Markets can swing hard for a few minutes. Wait for it to settle before a new trade.')}</span>
    </div>
  );
}

// --- Alerts ----------------------------------------------------------------
// Saved on this browser: an alert for every high-impact report (or high and medium) a set number
// of minutes before, plus alerts on single events. They ring while Market Matrix is open in a tab:
// a sound, a message in the corner, and a system notification if the browser allows it.
const ALERTS_KEY = 'mm-market-alerts';
export const ALERT_MINUTES = [5, 10, 15, 30];
function readAlerts() {
  try {
    const a = JSON.parse(localStorage.getItem(ALERTS_KEY));
    if (a && typeof a === 'object') return { auto: { minutes: 0, level: 'high', ...(a.auto || {}) }, events: a.events || {}, fired: a.fired || {} };
  } catch { /* nothing saved */ }
  return { auto: { minutes: 0, level: 'high' }, events: {}, fired: {} };
}
let alerts = readAlerts();
const alertListeners = new Set();
function saveAlerts(next, sync = true) {
  const changedSettings = next.auto !== alerts.auto || next.events !== alerts.events;
  alerts = next;
  try { localStorage.setItem(ALERTS_KEY, JSON.stringify(next)); } catch { /* only for this visit */ }
  alertListeners.forEach((fn) => fn());
  // A copy for push notifications, so they can ring with the site closed.
  if (sync && changedSettings) syncMarketAlerts(next);
}
function useAlerts() {
  return useSyncExternalStore((fn) => { alertListeners.add(fn); return () => alertListeners.delete(fn); }, () => alerts);
}
// The minutes before an event this trader wants an alert (0 = none).
function alertMinutes(e, a = alerts) {
  if (e.allDay) return 0;
  if (a.events[e.id] !== undefined) return a.events[e.id];
  if (!a.auto.minutes || e.type === 'fomcPress') return 0;
  return e.impact === 'high' || (a.auto.level === 'medium' && e.impact === 'medium') ? a.auto.minutes : 0;
}
// Asked for on a click, which is when browsers allow it; it also readies the sound.
function prepareAlerts() {
  ding();
  try { if ('Notification' in window && window.Notification.permission === 'default') window.Notification.requestPermission(); } catch { /* no notifications */ }
}

// A bell on one event: pick 5, 10, 15 or 30 minutes before, or off.
export function AlertBell({ e }) {
  const { t } = useI18n();
  const a = useAlerts();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (ev) => box.current && !box.current.contains(ev.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (e.allDay || new Date(e.at) < new Date() || (e.kind === 'earnings' && !e.timeKnown)) return null;
  const minutes = alertMinutes(e, a);
  const pick = (m) => {
    if (m) prepareAlerts();
    const events = { ...a.events, [e.id]: m };
    const fired = { ...a.fired };
    delete fired[e.id];
    saveAlerts({ ...a, events, fired });
    setOpen(false);
  };
  return (
    <span className="mkt-bell-wrap" ref={box}>
      <button type="button" className={`mkt-bell${minutes ? ' on' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        title={minutes ? t('Alert {n} min before', { n: minutes }) : t('Alert me')}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>
        {minutes ? <span>{t('{n} min', { n: minutes })}</span> : <span>{t('Alert me')}</span>}
      </button>
      {open && (
        <span className="mkt-bell-menu" role="menu">
          {ALERT_MINUTES.map((m) => (
            <button key={m} type="button" role="menuitemradio" aria-checked={minutes === m} className={minutes === m ? 'active' : ''} onClick={() => pick(m)}>
              {t('{n} min before', { n: m })}
            </button>
          ))}
          <button type="button" role="menuitemradio" aria-checked={!minutes} onClick={() => pick(0)}>{t('No alert')}</button>
        </span>
      )}
    </span>
  );
}

// "Alert me before every … report": the automatic rule.
export function AutoAlerts() {
  const { t } = useI18n();
  const a = useAlerts();
  const set = (patch) => {
    const auto = { ...a.auto, ...patch };
    if (auto.minutes) prepareAlerts();
    saveAlerts({ ...a, auto });
  };
  const denied = typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'denied';
  return (
    <div className="mkt-auto">
      <label>
        <span>{t('Alert me before')}</span>
        <select value={a.auto.level} onChange={(ev) => set({ level: ev.target.value })}>
          <option value="high">{t('high-impact reports')}</option>
          <option value="medium">{t('high and medium reports')}</option>
        </select>
        <select value={a.auto.minutes} onChange={(ev) => set({ minutes: Number(ev.target.value) })}>
          <option value={0}>{t('Off')}</option>
          {ALERT_MINUTES.map((m) => <option key={m} value={m}>{t('{n} min before', { n: m })}</option>)}
        </select>
      </label>
      <small>{denied
        ? t('Notifications are blocked in your browser, so alerts show inside Market Matrix with a sound.')
        : t('Alerts ring while Market Matrix is open in a browser tab, with a sound and a notification.')}</small>
      <PushHint />
    </div>
  );
}

// One place for alert messages (market events and personal reminders), in the corner of the screen.
let toastList = [];
const toastListeners = new Set();
export function pushToast(toast) {
  toastList = [...toastList.filter((x) => x.id !== toast.id), toast];
  toastListeners.forEach((fn) => fn());
}
function closeToast(id) {
  toastList = toastList.filter((x) => x.id !== id);
  toastListeners.forEach((fn) => fn());
}
export function ToastHost() {
  const { t } = useI18n();
  const list = useSyncExternalStore((fn) => { toastListeners.add(fn); return () => toastListeners.delete(fn); }, () => toastList);
  if (!list.length) return null;
  return (
    <div className="mkt-toasts" role="status">
      {list.map((toast) => (
        <div key={toast.id} className={`mkt-toast${toast.kind === 'reminder' ? ' reminder' : ''}`}>
          <strong>{toast.title}</strong>
          {toast.body && <span>{toast.body}</span>}
          <button type="button" onClick={() => closeToast(toast.id)} aria-label={t('Close')}>&times;</button>
        </div>
      ))}
    </div>
  );
}
export { ding };

// Runs in the background of the journal and rings each alert once.
export function MarketAlertRunner() {
  const { t } = useI18n();
  const a = useAlerts();
  const today = nyDay();
  const any = a.auto.minutes > 0 || Object.values(a.events).some(Boolean);
  const data = useMarketCalendar(any ? addDays(today, -1) : null, addDays(today, 14));
  const x = useTexts();
  useEffect(() => {
    if (!data || !data.events) return undefined;
    const check = () => {
      const now = Date.now();
      const due = data.events.filter((e) => {
        const m = alertMinutes(e, alerts);
        const at = new Date(e.at).getTime();
        return m && !alerts.fired[e.id] && now >= at - m * 60000 && now < at + 60000;
      });
      if (!due.length) return;
      const fired = { ...alerts.fired };
      due.forEach((e) => { fired[e.id] = Date.now(); });
      // Keep the record small: forget alerts older than a week.
      Object.keys(fired).forEach((id) => { if (Date.now() - fired[id] > 7 * 86400000) delete fired[id]; });
      saveAlerts({ ...alerts, fired });
      ding();
      due.forEach((e) => {
        const mins = Math.max(0, Math.round((new Date(e.at) - Date.now()) / 60000));
        const title = mins ? t('{title} in {n} min', { title: x.title(e), n: mins }) : t('{title} is out now', { title: x.title(e) });
        try {
          if ('Notification' in window && window.Notification.permission === 'granted') new window.Notification(title, { body: t(e.markets), tag: e.id });
        } catch { /* in-app message only */ }
        pushToast({ id: e.id, title, body: x.about(e) });
      });
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// The day pop-up of the Dashboard calendar: that day's events, each with its bell.
export function DayMarketEvents({ events }) {
  const { t } = useI18n();
  if (!events || !events.length) return null;
  return (
    <section className="day-mkt" aria-label={t('Market events')}>
      <span className="modal-section-label">{t('Market events')}</span>
      <ul className="mkt-list compact">{events.map((e) => <EventRow key={e.id} e={e} compact />)}</ul>
      {events.some((e) => e.kind === 'earnings') && <EarningsNote compact />}
    </section>
  );
}
